/**
 * Generato automaticamente.
 * framework-cfg-utenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient.ts';
import type { ErrorType } from '../../../client/apiClient.ts';
import type { GetMappaCatalogoLivelliParams } from '../schemas/getMappaCatalogoLivelliParams.ts';
import type { MappaCatalogoLivelliDTO } from '../schemas/mappaCatalogoLivelliDTO.ts';
import type { MappaCatalogoLivelliRequest } from '../schemas/mappaCatalogoLivelliRequest.ts';
import type { WsRispostaPaginataListMappaCatalogoLivelliDTO } from '../schemas/wsRispostaPaginataListMappaCatalogoLivelliDTO.ts';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce una configurazione del catalogo livelli mappa a partire dal suo id
 * @summary Dettaglio catalogo livelli mappa
 */
export const getMappaCatalogoLivelliId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<MappaCatalogoLivelliDTO>(
    {
      url: `/framework-cfg-utenti/mappa-catalogo-livelli/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetMappaCatalogoLivelliIdQueryKey = (id: string) =>
  [`/framework-cfg-utenti/mappa-catalogo-livelli/${id}`] as const;

export const getGetMappaCatalogoLivelliIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getMappaCatalogoLivelliId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getMappaCatalogoLivelliId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getMappaCatalogoLivelliId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetMappaCatalogoLivelliIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getMappaCatalogoLivelliId>>
  > = ({ signal }) => getMappaCatalogoLivelliId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetMappaCatalogoLivelliIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getMappaCatalogoLivelliId>>
>;
export type GetMappaCatalogoLivelliIdQueryError = ErrorType<unknown>;

/**
 * @summary Dettaglio catalogo livelli mappa
 */
export const useGetMappaCatalogoLivelliId = <
  TData = Awaited<ReturnType<typeof getMappaCatalogoLivelliId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getMappaCatalogoLivelliId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetMappaCatalogoLivelliIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Modifica una configurazione di livelli nel catalogo
 * @summary Aggiorna configurazione catalogo livelli mappa
 */
export const putMappaCatalogoLivelliId = (
  id: string,
  mappaCatalogoLivelliRequest: MappaCatalogoLivelliRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<MappaCatalogoLivelliDTO>(
    {
      url: `/framework-cfg-utenti/mappa-catalogo-livelli/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: mappaCatalogoLivelliRequest,
    },
    options,
  );
};

export const getPutMappaCatalogoLivelliIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putMappaCatalogoLivelliId>>,
    TError,
    { id: string; data: MappaCatalogoLivelliRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putMappaCatalogoLivelliId>>,
  TError,
  { id: string; data: MappaCatalogoLivelliRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putMappaCatalogoLivelliId>>,
    { id: string; data: MappaCatalogoLivelliRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putMappaCatalogoLivelliId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutMappaCatalogoLivelliIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putMappaCatalogoLivelliId>>
>;
export type PutMappaCatalogoLivelliIdMutationBody = MappaCatalogoLivelliRequest;
export type PutMappaCatalogoLivelliIdMutationError = ErrorType<unknown>;

/**
 * @summary Aggiorna configurazione catalogo livelli mappa
 */
export const usePutMappaCatalogoLivelliId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putMappaCatalogoLivelliId>>,
    TError,
    { id: string; data: MappaCatalogoLivelliRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutMappaCatalogoLivelliIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Rimuove una configurazione di livelli dal catalogo
 * @summary Elimina configurazione catalogo livelli mappa
 */
export const deleteMappaCatalogoLivelliId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-utenti/mappa-catalogo-livelli/${id}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteMappaCatalogoLivelliIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteMappaCatalogoLivelliId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteMappaCatalogoLivelliId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteMappaCatalogoLivelliId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteMappaCatalogoLivelliId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteMappaCatalogoLivelliIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteMappaCatalogoLivelliId>>
>;

export type DeleteMappaCatalogoLivelliIdMutationError = ErrorType<unknown>;

/**
 * @summary Elimina configurazione catalogo livelli mappa
 */
export const useDeleteMappaCatalogoLivelliId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteMappaCatalogoLivelliId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getDeleteMappaCatalogoLivelliIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ricerca paginata delle configurazioni del catalogo livelli mappa
 * @summary Ricerca catalogo livelli mappa
 */
export const getMappaCatalogoLivelli = (
  params?: GetMappaCatalogoLivelliParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListMappaCatalogoLivelliDTO>(
    {
      url: `/framework-cfg-utenti/mappa-catalogo-livelli`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetMappaCatalogoLivelliQueryKey = (
  params?: GetMappaCatalogoLivelliParams,
) =>
  [
    `/framework-cfg-utenti/mappa-catalogo-livelli`,
    ...(params ? [params] : []),
  ] as const;

export const getGetMappaCatalogoLivelliQueryOptions = <
  TData = Awaited<ReturnType<typeof getMappaCatalogoLivelli>>,
  TError = ErrorType<unknown>,
>(
  params?: GetMappaCatalogoLivelliParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getMappaCatalogoLivelli>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getMappaCatalogoLivelli>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetMappaCatalogoLivelliQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getMappaCatalogoLivelli>>
  > = ({ signal }) => getMappaCatalogoLivelli(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetMappaCatalogoLivelliQueryResult = NonNullable<
  Awaited<ReturnType<typeof getMappaCatalogoLivelli>>
>;
export type GetMappaCatalogoLivelliQueryError = ErrorType<unknown>;

/**
 * @summary Ricerca catalogo livelli mappa
 */
export const useGetMappaCatalogoLivelli = <
  TData = Awaited<ReturnType<typeof getMappaCatalogoLivelli>>,
  TError = ErrorType<unknown>,
>(
  params?: GetMappaCatalogoLivelliParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getMappaCatalogoLivelli>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetMappaCatalogoLivelliQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Inserisce una nuova configurazione di livelli della mappa nel catalogo
 * @summary Crea configurazione catalogo livelli mappa
 */
export const postMappaCatalogoLivelli = (
  mappaCatalogoLivelliRequest: MappaCatalogoLivelliRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<MappaCatalogoLivelliDTO>(
    {
      url: `/framework-cfg-utenti/mappa-catalogo-livelli`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: mappaCatalogoLivelliRequest,
    },
    options,
  );
};

export const getPostMappaCatalogoLivelliMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postMappaCatalogoLivelli>>,
    TError,
    { data: MappaCatalogoLivelliRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postMappaCatalogoLivelli>>,
  TError,
  { data: MappaCatalogoLivelliRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postMappaCatalogoLivelli>>,
    { data: MappaCatalogoLivelliRequest }
  > = props => {
    const { data } = props ?? {};

    return postMappaCatalogoLivelli(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostMappaCatalogoLivelliMutationResult = NonNullable<
  Awaited<ReturnType<typeof postMappaCatalogoLivelli>>
>;
export type PostMappaCatalogoLivelliMutationBody = MappaCatalogoLivelliRequest;
export type PostMappaCatalogoLivelliMutationError = ErrorType<unknown>;

/**
 * @summary Crea configurazione catalogo livelli mappa
 */
export const usePostMappaCatalogoLivelli = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postMappaCatalogoLivelli>>,
    TError,
    { data: MappaCatalogoLivelliRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostMappaCatalogoLivelliMutationOptions(options);

  return useMutation(mutationOptions);
};
